import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { trimmed, wordLimitIssues } from './_checks.js';
import { draftFields } from './_shared.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('info_accordion');

export const AccordionItem = z.looseObject({
  heading: z.string().optional(),
  content: z.string().optional(),
  emoji: z.string().optional(),
});

/** What one opened row can say before it stops being a glance. */
const ITEM_WORD_LIMIT = 50;

/** Rows the player opens one at a time, each with its own explanation behind it. */
export const infoAccordion: ActivityDefinition = {
  type: 'info_accordion',
  summary: 'An accordion whose rows each open to their own explanation.',
  setup: z.looseObject({
    ...draftFields,
    accordionItems: z.array(AccordionItem).optional(),
  }),
  check: (setup, ctx) => {
    const items = asList(setup.accordionItems);
    if (!items.length) {
      return [issue('empty', 'No rows are written — the accordion has nothing to open.', [...ctx.path, 'setup', 'accordionItems'], ctx)];
    }

    const issues = [];
    for (const [index, raw] of items.entries()) {
      const item = asRecord(raw);
      const path = [...ctx.path, 'setup', 'accordionItems', index];
      if (!trimmed(item.heading)) {
        issues.push(issue('itemHeading', `Row ${index + 1} has no heading to tap.`, [...path, 'heading'], ctx, raw));
      }
      issues.push(...wordLimitIssues({ type: 'info_accordion', text: item.content, limit: ITEM_WORD_LIMIT, what: `Row ${index + 1}`, path: [...path, 'content'], ctx }));
      if (!trimmed(item.content)) {
        issues.push(issue('itemContent', `Row ${index + 1} opens to nothing — it has no content.`, [...path, 'content'], ctx, raw));
      }
    }
    return issues;
  },
};
